import React, { useEffect, useState } from 'react';
import { api, fmtDate } from '../utils/api';
import Topbar from '../components/Topbar';
import { Badge, EmptyState, Spinner, SearchInput, useToast } from '../components/UI';

const FRANCHISEE_DOCS = ['ID', 'Proof of Address', 'Franchise Contract', 'ILA Agreement'];
const INVESTOR_DOCS   = ['ID', 'Proof of Address', 'ILA Agreement'];

export default function Compliance({ user }) {
  const [users, setUsers] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [compliance, setCompliance] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('franchisees');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null); // { user, type }
  const [showToast, Toast] = useToast();
  const [uploading, setUploading] = useState({});
  const [personalEmail, setPersonalEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [newDocLabel, setNewDocLabel] = useState('');
  const isAdmin = user?.role === 'admin' || user?.role === 'hq_support';
  const canModify = user?.role === 'admin';

  const load = async () => {
    const [u, f] = await Promise.all([api.getUsers(), api.getFranchises()]);
    const safeUsers = Array.isArray(u) ? u : [];
    const safeFranchises = Array.isArray(f) ? f : [];
    setUsers(safeUsers);
    setFranchises(safeFranchises);
    // Load all compliance docs
    const allUsers = safeUsers.filter(uu => uu.role === 'franchisee' || uu.role === 'investor');
    const compData = {};
    await Promise.all(allUsers.map(async uu => {
      const docs = await api.getCompliance(uu.id).catch(() => null);
      if (docs?.documents) {
        compData[uu.id] = {};
        docs.documents.forEach(d => { compData[uu.id][d.docType] = { name:d.fileName, uploadedAt:d.uploadedAt, id:d.id }; });
      }
    }));
    setCompliance(compData);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selected && users.length > 0) {
      const hash = window.location.hash.replace('#', '');
      const match = hash.match(/^compliance:([a-f0-9-]{36})$/);
      if (match) {
        const found = users.find(u => u.id === match[1]);
        if (found) setSelected({ user: found, type: found.role === 'investor' ? 'investor' : 'franchisee' });
      }
    }
  }, [users]);

  async function handleUpload(userId, docType, file) {
    const key = `${userId}_${docType}`;
    setUploading(u => ({...u, [key]:true}));
    const formData = new FormData();
    formData.append('file', file);
    formData.append('docType', docType);
    await api.uploadCompliance(userId, docType, formData);
    await api.logActivity('Uploaded compliance doc', `${users.find(u=>u.id===userId)?.name} — ${docType}`);
    showToast('Uploaded');
    setUploading(u => ({...u, [key]:false}));
    // Refresh this user's docs
    const docs = await api.getCompliance(userId).catch(() => null);
    if (docs?.documents) {
      const userDocs = {};
      docs.documents.forEach(d => { userDocs[d.docType] = { name:d.fileName, uploadedAt:d.uploadedAt, id:d.id }; });
      setCompliance(c => ({...c, [userId]:userDocs}));
    }
    if (selected?.user?.id === userId) {
      setSelected(s => ({...s, docs: docs?.documents || []}));
    }
  }

  async function handleDelete(userId, docType) {
    if (!window.confirm(`Remove ${docType}?`)) return;
    await api.deleteCompliance(userId, docType);
    await api.logActivity('Removed compliance doc', `${users.find(u=>u.id===userId)?.name} — ${docType}`);
    showToast('Removed');
    setCompliance(c => { const n={...c}; if(n[userId]) { n[userId]={...n[userId]}; delete n[userId][docType]; } return n; });
    if (selected?.user?.id === userId) {
      setSelected(s => ({...s, docs: (s.docs||[]).filter(d=>d.docType!==docType)}));
    }
  }

  async function handleDownload(userId, docType) {
    const res = await api.downloadCompliance(userId, docType);
    if (res?.url) window.open(res.url, '_blank');
    else showToast('Could not get download link');
  }

  const franchiseeUsers = users.filter(u => u.role === 'franchisee');
  const investorUsers = users.filter(u => u.role === 'investor');

  const targetUsers = (tab === 'franchisees' ? franchiseeUsers : investorUsers).filter(u => {
    if (!search) return true;
    return (u.name||'').toLowerCase().includes(search.toLowerCase()) || (u.email||'').toLowerCase().includes(search.toLowerCase());
  });

  if (loading) return <div style={{ marginLeft:'var(--sidebar-width)' }}><Topbar title="Compliance" /><Spinner /></div>;

  // ── Person detail view ──
  if (selected) {
    const { user: person, type } = selected;
    const docTypes = type === 'investor' ? INVESTOR_DOCS : FRANCHISEE_DOCS;
    const personDocs = compliance[person.id] || {};
    const uploaded = docTypes.filter(dt => personDocs[dt]).length;
    const personFranchises = franchises.filter(f =>
      type === 'investor'
        ? (f.investors||[]).some(i => (i.email||'').toLowerCase() === (person.email||'').toLowerCase())
        : (f.owners||[]).some(o => (o.email||'').toLowerCase() === (person.email||'').toLowerCase())
    );

    return (
      <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', flexDirection:'column', minHeight:'100vh' }}>
        <Topbar title={person.name} subtitle={`${person.email} · ${type}`} actions={
          <button className="btn btn-secondary btn-sm" onClick={() => { setSelected(null); window.location.hash = 'compliance'; }}>← Back</button>
        } />
        {Toast}
        <div style={{ padding:24 }}>
          {/* Header stats */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
            {/* Contact info */}
            <div className="card" style={{ padding:18 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:14 }}>CONTACT DETAILS</div>
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                <div><label>LOGIN EMAIL</label><div style={{ fontSize:13 }}>{person.email}</div></div>
                {person.phone && <div><label>PHONE</label><div style={{ fontSize:13 }}>{person.phone}</div></div>}
                <div><label>PERSONAL EMAIL</label>
                  <input type="email" value={personalEmail} onChange={e=>setPersonalEmail(e.target.value)} placeholder="personal@email.com" style={{ marginBottom:0 }} />
                </div>
                <div><label>NOTES</label>
                  <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3} placeholder="Any notes..." style={{ resize:'none', marginBottom:0 }} />
                </div>
              </div>
            </div>
            {/* Branches */}
            <div className="card" style={{ padding:18 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:14 }}>
                {type === 'investor' ? 'BRANCH INVESTMENTS' : 'ASSIGNED FRANCHISE'}
              </div>
              {personFranchises.length === 0 ? (
                <div style={{ fontSize:13, color:'var(--text-tertiary)' }}>No branches assigned</div>
              ) : personFranchises.map((f,i) => (
                <div key={i} style={{ padding:'8px 12px', background:'var(--blue-bg)', borderRadius:'var(--r-md)', marginBottom:6, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ fontSize:13, fontWeight:500 }}>{f.name}</span>
                  {type === 'investor' && <span style={{ fontSize:11, color:'var(--navy)' }}>{(f.investors||[]).find(i=>(i.email||'').toLowerCase()===(person.email||'').toLowerCase())?.sharePercent}%</span>}
                </div>
              ))}
            </div>
          </div>

          {/* Documents */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em' }}>DOCUMENTS</div>
            <div style={{ fontSize:13, fontWeight:700, color: uploaded===docTypes.length?'var(--green)':'var(--red)' }}>{uploaded}/{docTypes.length}</div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:14 }}>
            {docTypes.map(docType => {
              const existing = personDocs[docType];
              const uploading_key = `${person.id}_${docType}`;
              return (
                <div key={docType} className="card" style={{ padding:18 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                    <div>
                      <div style={{ fontWeight:600, fontSize:14 }}>{docType}</div>
                      {existing ? (
                        <div style={{ fontSize:11, color:'var(--green)', marginTop:4 }}>
                          ✓ {existing.name} · {fmtDate(existing.uploadedAt)}
                        </div>
                      ) : (
                        <div style={{ fontSize:11, color:'var(--red)', marginTop:4 }}>Not uploaded</div>
                      )}
                    </div>
                    <div style={{ width:10, height:10, borderRadius:'50%', background:existing?'var(--green)':'#e2e8f0', flexShrink:0, marginTop:4 }} />
                  </div>

                  {existing && (
                    <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                      <button className="btn btn-sm" style={{ background:'var(--blue-badge-bg)', color:'var(--blue)', border:'none', fontSize:11 }} onClick={() => handleDownload(person.id, docType)}>
                        <i className="ti ti-download" style={{ fontSize:12 }} /> View / Download
                      </button>
                      {canModify && (
                        <button className="btn btn-sm btn-danger" style={{ fontSize:11 }} onClick={() => handleDelete(person.id, docType)}>
                          Remove
                        </button>
                      )}
                    </div>
                  )}

                  {canModify && (
                    <label style={{ display:'block', cursor:'pointer' }}>
                      <div className="btn btn-secondary btn-sm" style={{ width:'100%', justifyContent:'center', fontSize:11, textAlign:'center' }}>
                        {uploading[uploading_key] ? 'Uploading...' : existing ? 'Replace File' : 'Upload File'}
                      </div>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{ display:'none' }}
                        onChange={e => { if (e.target.files[0]) handleUpload(person.id, docType, e.target.files[0]); e.target.value=''; }} />
                    </label>
                  )}
                </div>
              );
            })}
          </div>

          {/* Additional Documents */}
          <div style={{ marginTop:24 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', letterSpacing:'0.07em', marginBottom:12 }}>ADDITIONAL DOCUMENTS</div>
            {/* Show existing additional docs */}
            {(() => {
              const additionalDocs = Object.entries(personDocs).filter(([dt]) => !FRANCHISEE_DOCS.includes(dt) && !INVESTOR_DOCS.includes(dt));
              return additionalDocs.length > 0 ? (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:14, marginBottom:14 }}>
                  {additionalDocs.map(([docType, doc]) => (
                    <div key={docType} className="card" style={{ padding:18 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
                        <div>
                          <div style={{ fontWeight:600, fontSize:14 }}>{docType}</div>
                          <div style={{ fontSize:11, color:'var(--green)', marginTop:4 }}>
                            ✓ {doc.name} · {fmtDate(doc.uploadedAt)}
                          </div>
                        </div>
                        <div style={{ width:10, height:10, borderRadius:'50%', background:'var(--green)', flexShrink:0, marginTop:4 }} />
                      </div>
                      <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                        <button className="btn btn-sm" style={{ background:'var(--blue-badge-bg)', color:'var(--blue)', border:'none', fontSize:11 }} onClick={() => handleDownload(person.id, docType)}>
                          <i className="ti ti-download" style={{ fontSize:12 }} /> View / Download
                        </button>
                        {canModify && (
                          <button className="btn btn-sm btn-danger" style={{ fontSize:11 }} onClick={() => handleDelete(person.id, docType)}>
                            Remove
                          </button>
                        )}
                      </div>
                      {canModify && (
                        <label style={{ display:'block', cursor:'pointer' }}>
                          <div className="btn btn-secondary btn-sm" style={{ width:'100%', justifyContent:'center', fontSize:11, textAlign:'center' }}>
                            {uploading[`${person.id}_${docType}`] ? 'Uploading...' : 'Replace File'}
                          </div>
                          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{ display:'none' }}
                            onChange={e => { if (e.target.files[0]) handleUpload(person.id, docType, e.target.files[0]); e.target.value=''; }} />
                        </label>
                      )}
                    </div>
                  ))}
                </div>
              ) : null;
            })()}
            {/* Upload new additional doc */}
            {canModify && (
              <div className="card" style={{ padding:18 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'var(--navy)', marginBottom:12 }}>Upload Additional Document</div>
                <div style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
                  <div style={{ flex:1 }}>
                    <label>DOCUMENT NAME</label>
                    <input
                      value={newDocLabel}
                      onChange={e => setNewDocLabel(e.target.value)}
                      placeholder="e.g. Franchise Contract 2, DBS Check, Right to Work..."
                    />
                  </div>
                  <label style={{ cursor: newDocLabel.trim() ? 'pointer' : 'not-allowed', flexShrink:0 }}>
                    <div className="btn btn-secondary btn-sm" style={{ fontSize:11, opacity: newDocLabel.trim() ? 1 : 0.5, whiteSpace:'nowrap' }}>
                      {uploading[`${person.id}_${newDocLabel.trim()}`] ? 'Uploading...' : '+ Upload File'}
                    </div>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" style={{ display:'none' }}
                      disabled={!newDocLabel.trim()}
                      onChange={e => {
                        if (e.target.files[0] && newDocLabel.trim()) {
                          handleUpload(person.id, newDocLabel.trim(), e.target.files[0]);
                          setNewDocLabel('');
                          e.target.value='';
                        }
                      }} />
                  </label>
                </div>
                <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:6 }}>
                  Type the document name first, then click Upload File to choose the file.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── List view ──
  return (
    <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <Topbar title="Compliance" subtitle="Manage ID, contracts and ILA agreements" />
      {Toast}
      <div style={{ padding:24 }}>
        {/* Summary stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:20 }}>
          <div className="card" style={{ padding:'12px 16px' }}>
            <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>Franchisees</div>
            <div style={{ fontSize:22, fontWeight:700, color:'var(--navy)' }}>{franchiseeUsers.length}</div>
          </div>
          <div className="card" style={{ padding:'12px 16px' }}>
            <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>Investors</div>
            <div style={{ fontSize:22, fontWeight:700, color:'var(--navy)' }}>{investorUsers.length}</div>
          </div>
          <div className="card" style={{ padding:'12px 16px' }}>
            <div style={{ fontSize:11, color:'var(--text-tertiary)' }}>Fully Complete</div>
            <div style={{ fontSize:22, fontWeight:700, color:'var(--green)' }}>
              {[...franchiseeUsers, ...investorUsers].filter(u => {
                const docTypes = u.role==='investor' ? INVESTOR_DOCS : FRANCHISEE_DOCS;
                const docs = compliance[u.id] || {};
                return docTypes.every(dt => docs[dt]);
              }).length}
            </div>
          </div>
        </div>

        {/* Tabs + Search */}
        <div style={{ display:'flex', gap:8, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
          <div style={{ display:'flex', gap:4 }}>
            {['franchisees','investors'].map(t => (
              <button key={t} onClick={()=>setTab(t)} className="btn btn-sm" style={{
                background:tab===t?'var(--navy)':'#fff', color:tab===t?'#fff':'var(--text-secondary)',
                border:'1px solid var(--border)', textTransform:'capitalize',
              }}>{t} ({t==='franchisees'?franchiseeUsers.length:investorUsers.length})</button>
            ))}
          </div>
          <div style={{ flex:1, maxWidth:280 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search by name..." />
          </div>
        </div>

        {/* Cards grid — clickable */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
          {targetUsers.map(u => {
            const docTypes = tab==='investors' ? INVESTOR_DOCS : FRANCHISEE_DOCS;
            const docs = compliance[u.id] || {};
            const uploaded = docTypes.filter(dt => docs[dt]).length;
            const complete = uploaded === docTypes.length;
            const pct = Math.round((uploaded/docTypes.length)*100);
            return (
              <div key={u.id} className="card" style={{ padding:'14px 16px', cursor:'pointer', border:`1px solid ${complete?'rgba(15,110,86,0.2)':'rgba(28,25,96,0.08)'}`, transition:'border-color 0.15s' }}
                onClick={() => (() => { const _s = { user:u, type: tab==='investors'?'investor':'franchisee' }; setSelected(_s); window.location.hash = 'compliance:' + u.id; })()}
                onMouseEnter={e=>e.currentTarget.style.borderColor='var(--navy)'}
                onMouseLeave={e=>e.currentTarget.style.borderColor=complete?'rgba(15,110,86,0.2)':'rgba(28,25,96,0.08)'}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                  <div style={{ width:34, height:34, borderRadius:'50%', background:'var(--navy)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'#fff', flexShrink:0 }}>
                    {(u.name||'?').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:13, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.name}</div>
                    <div style={{ fontSize:11, color:'var(--text-tertiary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{u.email}</div>
                  </div>
                  <i className="ti ti-chevron-right" style={{ fontSize:14, color:'var(--text-tertiary)', flexShrink:0 }} />
                </div>
                {/* Progress bar */}
                <div style={{ height:4, background:'#f1f5f9', borderRadius:2, marginBottom:6 }}>
                  <div style={{ height:'100%', borderRadius:2, width:`${pct}%`, background:complete?'var(--green)':pct>50?'var(--amber)':'var(--red)', transition:'width 0.3s' }} />
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <div style={{ display:'flex', gap:4 }}>
                    {docTypes.map(dt => (
                      <div key={dt} title={dt} style={{ width:8, height:8, borderRadius:'50%', background:docs[dt]?'var(--green)':'#e2e8f0' }} />
                    ))}
                  </div>
                  <div style={{ fontSize:11, fontWeight:600, color:complete?'var(--green)':pct>50?'var(--amber)':'var(--red)' }}>{uploaded}/{docTypes.length}</div>
                </div>
              </div>
            );
          })}
          {targetUsers.length === 0 && (
            <div style={{ gridColumn:'1/-1' }}>
              <EmptyState icon="ti-file-check" message="No users found" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
